import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import {
  deprecatedBEDateFormat,
  deserializeYesplanAPIDate,
  getWeekAfter,
  getWeekBefore,
  getWeekNumber,
  getWeekOf,
  getYearOfWeek,
  internalDateFormat,
  isDayPartOfWeek,
  serializeYesplanAPIDate,
  todayDateObject
} from '../libs/dateTimeUtilities';
import NavigationHistory from '../libs/NavigationHistory';
import { capture, PH_WEEK_NAVIGATED } from '../libs/posthogAnalytics';

export default class DateNavigationUrlWrapper extends React.Component {
  static getWeekAndDayPatternUrl(week, dateObjectOrNull) {
    const weekNumber = getWeekNumber(week);
    const year = getYearOfWeek(week);
    if (dateObjectOrNull !== null) {
      const dayString = serializeYesplanAPIDate(dateObjectOrNull);
      if (dayString && isDayPartOfWeek(dateObjectOrNull, week))
        return `/week-${weekNumber}-${year}#${dayString}`;
    }
    return `/week-${weekNumber}-${year}`;
  }

  static getDateAndWeekFromUrlPartsOrFromCurrentDate(
    weekOrDatePattern,
    hashPattern,
    takeFirstDayOfWeekForOtherWeeks = false
  ) {
    if (!weekOrDatePattern) {
      const now = todayDateObject();
      return {
        dateObject: now,
        week: getWeekOf(now)
      };
    }
    /* Date in the hashPattern, supports BE format and ISO format. Use ISO internaly. */
    const parsedDates = [deprecatedBEDateFormat, internalDateFormat].map((pattern) =>
      moment(hashPattern || weekOrDatePattern, pattern, true)
    );
    const parsedDateOrToday =
      parsedDates.find((date) => date.isValid()) || todayDateObject();
    let dateObjectForShortcutString;
    switch (weekOrDatePattern) {
      case 'today':
      case 'thisweek':
        return {
          dateObject: parsedDateOrToday,
          week: getWeekOf(todayDateObject())
        };
      case 'tomorrow':
        dateObjectForShortcutString = todayDateObject().add(1, 'd');
        return {
          dateObject: dateObjectForShortcutString,
          week: getWeekOf(dateObjectForShortcutString)
        };
      case 'yesterday':
        dateObjectForShortcutString = todayDateObject().add(-1, 'd');
        return {
          dateObject: dateObjectForShortcutString,
          week: getWeekOf(dateObjectForShortcutString)
        };
      case 'nextweek':
        dateObjectForShortcutString = todayDateObject().add(7, 'd');
        return {
          dateObject: parsedDateOrToday,
          week: getWeekOf(dateObjectForShortcutString)
        };
      case 'previousweek':
        dateObjectForShortcutString = todayDateObject().add(-7, 'd');
        return {
          dateObject: parsedDateOrToday,
          week: getWeekOf(dateObjectForShortcutString)
        };
      default:
        /* Pattern week-xx-yyyy */
        const weekExpression = /week-\d\d?-\d{4}/g;
        if (weekOrDatePattern.match(weekExpression)) {
          const weekNumber = weekOrDatePattern.match(/\d\d?(?=-)/g)[0];
          const yearNumber = weekOrDatePattern.match(/\d{4}/g)[0];
          const weekStart = deserializeYesplanAPIDate(
            `${yearNumber}-01-01`
          ) /* Be sure to take a parsedDateOrToday in the correct year here, otherwise a wrong year could be used if using this in the last week of the year */
            .isoWeekYear(yearNumber)
            .isoWeek(weekNumber)
            .startOf('isoWeek');
          const isWeekCurrentWeek =
            getWeekNumber(getWeekOf(todayDateObject())) === Number(weekNumber);
          return {
            dateObject:
              takeFirstDayOfWeekForOtherWeeks && !hashPattern && !isWeekCurrentWeek
                ? weekStart
                : parsedDateOrToday,
            week: getWeekOf(weekStart)
          };
        }

        return {
          dateObject: parsedDateOrToday,
          week: getWeekOf(parsedDateOrToday)
        };
    }
  }

  static getDateFromUrlPartsOrFromCurrentDate(date) {
    const today = todayDateObject();
    if (!date) return null;

    /* BE format and ISO format are accepted. Use ISO internaly. */
    const parsedDates = [deprecatedBEDateFormat, internalDateFormat].map((pattern) =>
      moment(date, pattern, true)
    );
    const parsedDateOrToday =
      parsedDates.find((parsedDate) => parsedDate.isValid()) || null;
    let dateObjectForShortcutString;
    switch (date) {
      case 'today':
        return today;
      case 'yesterday':
        dateObjectForShortcutString = today.add(-1, 'd');
        return dateObjectForShortcutString;
      default:
        return parsedDateOrToday;
    }
  }

  getUrlForWeekAndDay(widgetName, week, dateObjectOrNull) {
    const { linkUrlFor } = this.props;
    return linkUrlFor(
      `/${widgetName}${DateNavigationUrlWrapper.getWeekAndDayPatternUrl(
        week,
        dateObjectOrNull
      )}`
    );
  }

  getDateAndWeekFromUrlOrFromCurrentDate(
    widgetName,
    takeFirstDayOfWeekForOtherWeeks = false
  ) {
    const { location } = this.props;
    const datePattern = location.pathname.split(`/${widgetName}/`)[1];
    let hashPattern = location.hash.substr(1);
    /* Could be that the hash in props.location is not updated because of rewrites, try to get it out of the url if present */
    // eslint-disable-next-line prefer-destructuring
    if (datePattern && datePattern.includes('#')) hashPattern = datePattern.split('#')[1];
    const { dateObject, week } =
      DateNavigationUrlWrapper.getDateAndWeekFromUrlPartsOrFromCurrentDate(
        datePattern,
        hashPattern,
        takeFirstDayOfWeekForOtherWeeks
      );
    return {
      dateObject,
      week
    };
  }

  getDateFromUrlOrFromCurrentDate(widgetName) {
    const { location } = this.props;
    const dateString = location.pathname.split(`/${widgetName}/`)[1];
    return DateNavigationUrlWrapper.getDateFromUrlPartsOrFromCurrentDate(dateString);
  }

  goToUrlForNextWeek(widgetName, currentWeek) {
    const weekAfter = getWeekAfter(currentWeek);
    capture(PH_WEEK_NAVIGATED, {
      direction: 'next',
      widgetName,
      goToWeek: {
        startDate: serializeYesplanAPIDate(weekAfter.startDateObject),
        endDate: serializeYesplanAPIDate(weekAfter.endDateObject)
      }
    });
    this.goToUrlForWeekAndDate(widgetName, weekAfter, null, true);
  }

  goToUrlForPreviousWeek(widgetName, currentWeek) {
    const weekBefore = getWeekBefore(currentWeek);
    capture(PH_WEEK_NAVIGATED, {
      direction: 'previous',
      widgetName,
      goToWeek: {
        startDate: serializeYesplanAPIDate(weekBefore.startDateObject),
        endDate: serializeYesplanAPIDate(weekBefore.endDateObject)
      }
    });
    this.goToUrlForWeekAndDate(widgetName, weekBefore, null, true);
  }

  goToUrlForWeekAndDate(
    widgetName,
    week,
    dateObject,
    replace = false,
    sendPageviewOnReplace = true
  ) {
    const captureData = {
      week: {
        startDate: serializeYesplanAPIDate(week.startDateObject),
        endDate: serializeYesplanAPIDate(week.endDateObject)
      }
    };
    // Only include date if dateObject is valid
    if (dateObject && dateObject instanceof moment && dateObject.isValid()) {
      captureData.date = serializeYesplanAPIDate(dateObject);
    }

    capture(PH_WEEK_NAVIGATED, { ...captureData, direction: 'specific' });
    const { navigationHistory } = this.props;
    navigationHistory.debouncedGoTo(
      this.getUrlForWeekAndDay(widgetName, week, dateObject),
      replace,
      {},
      sendPageviewOnReplace
    );
  }

  replaceUrlWithWeekAndDateUrl(widgetName, takeFirstDayOfWeekForOtherWeeks = false) {
    const { location, linkUrlFor, history } = this.props;
    const { week, dateObject } = this.getDateAndWeekFromUrlOrFromCurrentDate(
      widgetName,
      takeFirstDayOfWeekForOtherWeeks
    );
    const currentUrlPath = location.pathname + (location.hash || '');
    const expectedUrl = `${linkUrlFor(
      `/${widgetName}`
    )}${DateNavigationUrlWrapper.getWeekAndDayPatternUrl(week, dateObject)}`;
    if (currentUrlPath !== expectedUrl) history.replace(expectedUrl, location.state);
  }

  replaceUrlWithDateUrl(widgetName) {
    const { location, linkUrlFor, history } = this.props;
    const dateObject = this.getDateFromUrlOrFromCurrentDate(widgetName);
    const dateString = serializeYesplanAPIDate(dateObject);
    const currentUrlPath = location.pathname + (location.hash || '');
    const url = `${linkUrlFor(`/${widgetName}`)}/${dateString}`;
    if (currentUrlPath !== url) history.replace(url, location.state);
  }

  render() {
    const { children } = this.props;
    return children({
      ...omit(this.props, ['children']),
      getWeekAndDayPatternUrl: DateNavigationUrlWrapper.getWeekAndDayPatternUrl,
      getDateAndWeekFromUrlOrFromCurrentDate:
        this.getDateAndWeekFromUrlOrFromCurrentDate.bind(this),
      getDateFromUrlOrFromCurrentDate: this.getDateFromUrlOrFromCurrentDate.bind(this),
      getUrlForWeekAndDay: this.getUrlForWeekAndDay.bind(this),
      replaceUrlWithWeekAndDateUrl: this.replaceUrlWithWeekAndDateUrl.bind(this),
      replaceUrlWithDateUrl: this.replaceUrlWithDateUrl.bind(this),
      goToUrlForNextWeek: this.goToUrlForNextWeek.bind(this),
      goToUrlForPreviousWeek: this.goToUrlForPreviousWeek.bind(this),
      goToUrlForWeekAndDate: this.goToUrlForWeekAndDate.bind(this)
    });
  }
}

DateNavigationUrlWrapper.propTypes = {
  children: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    hash: PropTypes.string
  }).isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired
  }).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired
};
